import CryptoJS from "crypto-js";
import Select from "react-select";

function defaultDateFormatter(dateString) {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function defaultDateFormat(dateString) {
  if (!dateString) return "--";

  const [year, month, day] = dateString.split("-");

  const date = new Date(Number(year), Number(month) - 1, Number(day));
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}
const ENCODE_KEY = process.env.LINK_ENCODE_KEY;

const getKeyAndIV = () => {
  const hashed = CryptoJS.SHA256(ENCODE_KEY); // returns a WordArray
  const key = hashed; // use full hash as key (32 bytes)

  // Laravel takes first 16 ASCII characters from SHA256 hash (not hex or binary)
  const hashString = hashed.toString(CryptoJS.enc.Latin1); // use Latin1 to mimic raw binary-to-ascii
  const iv = CryptoJS.enc.Latin1.parse(hashString.substring(0, 16));

  return { key, iv };
};

function encryptDecrypt(action, value) {
  const { key, iv } = getKeyAndIV();

  if (value === null || value === undefined) return false;

  const inputValue = String(value);

  if (action === "e") {
    const encrypted = CryptoJS.AES.encrypt(inputValue, key, {
      iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    });
    return CryptoJS.enc.Base64.stringify(encrypted.ciphertext);
  } else if (action === "d") {
    try {
      const decrypted = CryptoJS.AES.decrypt(
        { ciphertext: CryptoJS.enc.Base64.parse(inputValue) },
        key,
        {
          iv,
          mode: CryptoJS.mode.CBC,
          padding: CryptoJS.pad.Pkcs7,
        }
      );
      return decrypted.toString(CryptoJS.enc.Utf8);
    } catch (err) {
      console.error("Decryption error:", err);
      return false;
    }
  }

  return false;
}

const modalStyle = {
  position: "fixed",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  backgroundColor: "rgba(0,0,0,0.5)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  zIndex: "999",
};

const modalContentStyle = {
  backgroundColor: "#fff",
  padding: "5px",
  // borderRadius: '10px',
  width: "90%",
  // maxWidth: '1200px'
};

const formatDateToLocalISOString = (date) => {
  const pad = (num) => String(num).padStart(2, "0");

  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());
  const seconds = pad(date.getSeconds());

  const offsetMinutes = -date.getTimezoneOffset();
  const sign = offsetMinutes >= 0 ? "+" : "-";
  const offsetHours = pad(Math.floor(Math.abs(offsetMinutes) / 60));
  const offsetMins = pad(Math.abs(offsetMinutes) % 60);

  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${sign}${offsetHours}:${offsetMins}`;
};

const CustomSelect = ({
  name,
  value,
  options,
  onChange,
  disabled = false,
  ...props
}) => (
  <Select
    name={name}
    value={options.find((opt) => opt.value === value)}
    options={options}
    onChange={(selectedOption) =>
      onChange({
        target: {
          name,
          value: selectedOption?.value ?? "",
        },
      })
    }
    isDisabled={disabled}
    {...props}
  />
);

function formatDateTime(dateTimeStr) {
  if (!dateTimeStr) return "--";
  const date = new Date(dateTimeStr.replace(" ", "T"));

  const day = date.getDate().toString().padStart(2, "0");
  const month = date.toLocaleString("en-US", { month: "short" });
  const year = date.getFullYear();

  const hours = date.getHours().toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");

  return `${day} ${month} ${year} ${hours}:${minutes}`;
}

const formatDateToYYYYMMDD = (date) => {
  if (!date) return null;

  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

// ---- Global Payroll display dates: DD-MMM-YYYY ---------------------------
// Display only - API payloads keep YYYY-MM-DD (formatDateToYYYYMMDD above).
// A date-only string ("2026-10-08", or a leading date of an ISO string with
// a "T00:00:00.000Z" tail) is read by its own parts, never through Date(),
// so a timezone can't shift the business day.
const DISPLAY_MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const formatDisplayDate = (value, fallback = "") => {
  if (value === null || value === undefined || value === "") return fallback;
  if (typeof value === "string") {
    const v = value.trim();
    const m = /^(\d{4})-(\d{2})-(\d{2})(?:$|[T\s])/.exec(v);
    // date-only, or a midnight-UTC timestamp standing in for one
    if (m && (v.length === 10 || /^.{10}[T\s]00:00(:00(\.0+)?)?Z?$/.test(v))) {
      return `${m[3]}-${DISPLAY_MONTHS[Number(m[2]) - 1]}-${m[1]}`;
    }
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return fallback;
  return `${String(d.getDate()).padStart(2, "0")}-${DISPLAY_MONTHS[d.getMonth()]}-${d.getFullYear()}`;
};

// Date + time (project-local time, as before): 08-Oct-2026 14:05
const formatDisplayDateTime = (value, fallback = "") => {
  const d = new Date(value);
  if (value === null || value === undefined || value === "" || Number.isNaN(d.getTime())) return fallback;
  return `${formatDisplayDate(d)} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

// "08-Oct-2026 to 08-Oct-2026"
const formatDisplayPeriod = (start, end) => `${formatDisplayDate(start)} to ${formatDisplayDate(end)}`;

// Month-only values ("2026-09") are not a day, so they keep just month + year: Sep-2026
const formatDisplayMonth = (value, fallback = "") => {
  const m = /^(\d{4})-(\d{2})/.exec(String(value ?? ""));
  return m ? `${DISPLAY_MONTHS[Number(m[2]) - 1]}-${m[1]}` : fallback || value || "";
};

function capitalizeFirstLetter(str) {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
}

function encryptDecryptId(action, data) {
  const idSecret = process.env.REACT_APP_ID_ENCODE_KEY;
  if (!idSecret) throw new Error("Missing encryption key");

  if (action === "encrypt") {
    let encrypted = CryptoJS.AES.encrypt(data.toString(), idSecret).toString();
    encrypted = encrypted.replace(/\//g, "_").replace(/\+/g, "-");
    return encrypted;
  } else if (action === "decrypt") {
    const base64 = data.replace(/_/g, "/").replace(/-/g, "+");
    const bytes = CryptoJS.AES.decrypt(base64, idSecret);
    return bytes.toString(CryptoJS.enc.Utf8);
  } else {
    throw new Error("Invalid action. Use 'encrypt' or 'decrypt'.");
  }
}

const formatDateToHHMM = (date) => {
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${hours}:${minutes}`;
};

const getCurrentFinancialYear = () => {
  const now = new Date();
  const month = now.getMonth() + 1;
  const year = now.getFullYear();

  return month >= 4 ? `${year}-${year + 1}` : `${year - 1}-${year}`;
};

const generateFinancialYears = (count = 5) => {
  const currentFY = getCurrentFinancialYear();
  const startYear = parseInt(currentFY.split("-")[0]);

  return Array.from({ length: count }, (_, i) => {
    const y = startYear - i;
    return `${y}-${y + 1}`;
  });
};

const stripHtml = (html) => {
  if (!html) return "";
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = html;
  return tempDiv.textContent || tempDiv.innerText || "";
};

const cleanHtml = (html) => {
  if (!html) return "";

  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = html;

  // Remove scripts (security)
  tempDiv.querySelectorAll("script").forEach(el => el.remove());

  return tempDiv.innerHTML; // ✅ keep iframe
};

// utils/sortData.js

const sortData = (data, field, order = "asc") => {
  return [...data].sort((a, b) => {
    const aValue = a[field];
    const bValue = b[field];

    // Handle null/undefined
    if (aValue == null && bValue == null) return 0;
    if (aValue == null) return order === "asc" ? 1 : -1;
    if (bValue == null) return order === "asc" ? -1 : 1;

    // String comparison
    if (typeof aValue === "string" && typeof bValue === "string") {
      return order === "asc"
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }

    // Number comparison
    if (typeof aValue === "number" && typeof bValue === "number") {
      return order === "asc"
        ? aValue - bValue
        : bValue - aValue;
    }

    // Date comparison
    if (
      (aValue instanceof Date || !isNaN(Date.parse(aValue))) &&
      (bValue instanceof Date || !isNaN(Date.parse(bValue)))
    ) {
      const dateA = new Date(aValue);
      const dateB = new Date(bValue);

      return order === "asc"
        ? dateA - dateB
        : dateB - dateA;
    }

    // Default comparison
    return order === "asc"
      ? aValue > bValue
        ? 1
        : -1
      : aValue < bValue
        ? 1
        : -1;
  });
};

export {
  defaultDateFormatter,
  modalStyle,
  modalContentStyle,
  encryptDecrypt,
  CustomSelect,
  formatDateToLocalISOString,
  formatDateTime,
  formatDateToYYYYMMDD,
  formatDisplayDate,
  formatDisplayDateTime,
  formatDisplayPeriod,
  formatDisplayMonth,
  capitalizeFirstLetter,
  encryptDecryptId,
  formatDateToHHMM,
  getCurrentFinancialYear,
  generateFinancialYears,
  defaultDateFormat,
  stripHtml,
  cleanHtml,
  sortData
};
